import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { NftView } from "../types";
import { NftCard } from "./NftInventory";

/** Container scrolling, with a fixed body height and square art for predictable rows. */
export function VirtualNftGrid({ items, onSelect, onMetadataMismatch }: {
  items: NftView[]; onSelect(nft: NftView): void; onMetadataMismatch?: (() => void) | undefined;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ columns: 4, width: 1000, top: 0, viewport: 800, gap: 16, bodyHeight: 156, border: 1 });
  const [focused, setFocused] = useState<number | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const previous = useRef<{ width: number; columns: number; rowHeight: number; top: number } | null>(null);
  const pendingScroll = useRef<number | null>(null);
  useLayoutEffect(() => {
    const element = grid.current!;
    const container = scroller.current!;
    let frame = 0;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      const columns = style.gridTemplateColumns.split(" ").filter(Boolean).length || 1;
      const width = rect.width || 1000;
      const gap = parseFloat(style.columnGap) || 16;
      const bodyHeight = parseFloat(style.getPropertyValue("--nft-card-body-height")) || 156;
      const border = parseFloat(style.getPropertyValue("--nft-card-border-width")) || 1;
      // The square frame uses the card's inner width; two outer borders add
      // back exactly what that width loses. CSS owns both layout values.
      const rowHeight = (width - gap * (columns - 1)) / columns + bodyHeight + gap;
      let top = container.scrollTop;
      const old = previous.current;
      if (old && (old.width !== width || old.columns !== columns || old.rowHeight !== rowHeight) && old.top > 0) {
        const index = Math.floor(old.top / old.rowHeight) * old.columns;
        const nextTop = Math.floor(index / columns) * rowHeight + (old.top % old.rowHeight) / old.rowHeight * rowHeight;
        pendingScroll.current = nextTop;
        top = nextTop;
      }
      previous.current = { width, columns, rowHeight, top };
      setLayout({ columns, width, gap, bodyHeight, border, top, viewport: container.clientHeight });
    };
    let needsMeasurement = false;
    const schedule = (geometry: boolean) => {
      needsMeasurement ||= geometry;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (needsMeasurement) { needsMeasurement = false; measure(); return; }
        const top = container.scrollTop;
        const viewport = container.clientHeight;
        if (previous.current) previous.current.top = top;
        setLayout(current => current.top === top && current.viewport === viewport ? current : { ...current, top, viewport });
      });
    };
    const resize = () => schedule(true);
    const scroll = () => schedule(false);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    observer?.observe(element);
    observer?.observe(container);
    container.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); container.removeEventListener("scroll", scroll);
      window.removeEventListener("resize", resize); };
  }, []);

  useLayoutEffect(() => {
    const scrollTop = pendingScroll.current;
    if (scrollTop === null) return;
    pendingScroll.current = null;
    // Apply the anchor after the new rows and container scroll height are committed.
    // Otherwise a desktop-to-mobile jump near the tail can be clamped against
    // the old, shorter grid or briefly point at unmounted rows.
    // Absolute + instant also avoids native scroll clamping during a shorter
    // grid commit and smooth-scroll animation changing the anchor.
    scroller.current?.scrollTo({ top: scrollTop, behavior: "instant" });
  }, [layout]);

  useLayoutEffect(() => {
    if (target === null) return;
    const button = grid.current?.querySelector<HTMLButtonElement>(`[data-nft-index="${target}"] button`);
    button?.focus({ preventScroll: true });
    button?.scrollIntoView?.({ block: "nearest" });
    setTarget(null);
  }, [target]);

  const { columns, width, gap, bodyHeight, border } = layout;
  const cardWidth = (width - gap * (columns - 1)) / columns;
  const stride = cardWidth + bodyHeight + gap;
  const rows = Math.ceil(items.length / columns);
  const first = Math.max(0, Math.min(rows - 1, Math.floor(layout.top / stride)) - 2);
  const last = Math.min(rows, Math.ceil((layout.top + layout.viewport) / stride) + 2);
  const indices = new Set<number>();
  for (let i = first * columns; i < Math.min(items.length, last * columns); i++) indices.add(i);
  if (focused !== null && focused < items.length) indices.add(focused);
  if (target !== null && target < items.length) indices.add(target);
  return <div ref={scroller} className="virtual-nft-grid-scroll">
    <div ref={grid} className="nft-grid virtual-nft-grid" role="list" aria-label="Loaded NFTs"
    style={{ height: Math.max(0, rows * stride - gap), "--nft-image-size": `${cardWidth - 2 * border}px` } as CSSProperties}
    onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(null); }}
    onKeyDown={(event) => {
      if (event.key !== "Tab" || focused === null) return;
      const next = focused + (event.shiftKey ? -1 : 1);
      if (next < 0 || next >= items.length) return;
      event.preventDefault(); setTarget(next); setFocused(next);
    }}>
    {[...indices].sort((a, b) => a - b).map((index) => {
      const item = items[index];
      if (!item) return null;
      return <div role="listitem" aria-posinset={index + 1}
      aria-setsize={items.length} data-nft-index={index} key={`${item.collectionAddress}:${item.tokenId}`}
      onFocus={() => setFocused(index)} style={{ position: "absolute", top: Math.floor(index / columns) * stride,
        left: (index % columns) * (cardWidth + gap), width: cardWidth, height: stride - gap }}>
      <NftCard nft={item} onOpen={() => onSelect(item)} onMetadataMismatch={onMetadataMismatch} />
    </div>; })}
    </div>
  </div>;
}
